import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForNutritionistsPageFr() {
  return (
    <>
      <Head>
        <title>
          Featcal pour Nutritionnistes | Meilleur suivi, moins d’admin
        </title>
        <meta
          name="description"
          content="Featcal aide les nutritionnistes à structurer le suivi : journaux, plans, et synthèses prêtes à la revue pour améliorer l’adhérence entre séances (perte et prise de poids)."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            Pour les professionnels de la nutrition
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Transformez le suivi entre séances en système répétable.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal maintient la régularité entre séances via un suivi
            structuré, des plans clairs et une revue rapide-pour moins de
            relances et plus de résultats.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Commencer gratuitement
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              Voir la FAQ
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              Le vrai sujet : la régularité entre les séances
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Les journaux prennent trop de temps à relire.",
                "Les plans se perdent : le client ne sait plus quoi faire.",
                "L’adhérence baisse entre les séances, et la séance devient du rattrapage.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Revue journaux plus rapide",
                desc: "Synthèses propres et patterns actionnables-sans décodage manuel.",
              },
              {
                title: "Plus d’adhérence entre séances",
                desc: "Plans dans l’app, rappels, et accountability au quotidien : les clients savent quoi faire ensuite.",
              },
              {
                title: "Un programme de suivi plus clair",
                desc: "Plans, ressources et feedback au même endroit : la valeur du suivi devient plus facile à comprendre et à vendre.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Idéal pour
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Programmes perte / prise de poids avec revue hebdo.",
                  "Changement d’habitudes (protéines, timing, routines).",
                  "Digestif : symptômes + patterns alimentaires.",
                  "Nutrition sportive sans chaos Excel.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Ce que Featcal remplace
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Décodage manuel des journaux + tableurs.",
                  "Captures, DMs et notes dispersées.",
                  "Plans perdus et messages “je fais quoi ensuite ?”.",
                  "Relances pour l’adhérence entre les séances.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Journal alimentaire"
          title="Relire en minutes, pas en heures"
          description="Une vue claire des entrées et des patterns pour préparer vos séances plus vite-sans décodage manuel."
          bullets={[
            "Repérez les patterns et les écarts plus rapidement.",
            "Préparez avec un snapshot simple.",
            "Plus de temps pour coacher, moins pour trier.",
          ]}
          imageSrc="/images/user-food-log.webp"
          imageAlt="Aperçu du journal alimentaire"
          align="left"
          imageFit="contain"
        />

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Prêt à améliorer l’adhérence entre les séances ?
                </h2>
                <p className="text-slate-700 mt-2">
                  Commencez gratuitement et invitez un client en quelques
                  minutes.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Commencer gratuitement
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  Voir la FAQ
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
